/**
 * PROFILE MANAGEMENT PAGE
 * 
 * Comprehensive profile management interface with:
 * - Create/edit user profile (display name, avatar, bio)
 * - Wallet verification flow
 * - View all linked wallets with verification status
 * - Unlink wallets
 */

import { useState, useEffect } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { supabase } from '../lib/supabase';
import VerifyWalletButton from '../components/VerifyWalletButton';
import LinkedWallets from '../components/LinkedWallets';
import VerifiedBadge from '../components/VerifiedBadge';
import { useWalletProfile, useCreateProfile } from '../hooks/useWalletProfile';

export default function ProfileManagement() {
  const { publicKey } = useWallet();
  const walletAddress = publicKey?.toBase58();

  const { profile, loading: profileLoading, refresh: refreshProfile } = useWalletProfile(walletAddress);
  const { createProfile, creating } = useCreateProfile();

  const [isEditing, setIsEditing] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [bio, setBio] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [verificationType, setVerificationType] = useState('none');

  // Update form when profile loads
  useEffect(() => {
    if (profile) {
      setDisplayName(profile.displayName || '');
      setAvatarUrl(profile.avatarUrl || '');
      setBio(profile.bio || '');
    }
  }, [profile]);

  // Fetch verification type from creators table
  useEffect(() => {
    const fetchVerificationType = async () => {
      if (!walletAddress) return;

      try {
        const { data, error } = await supabase
          .from('creators')
          .select('verification_type')
          .eq('wallet', walletAddress)
          .maybeSingle();

        if (!error && data) {
          setVerificationType(data.verification_type || 'none');
        }
      } catch (err) {
        console.error('Error fetching verification type:', err);
      }
    };

    fetchVerificationType();
  }, [walletAddress]);

  const handleCreateProfile = async () => {
    if (!displayName.trim()) {
      setError('Display name is required');
      return;
    }

    setError(null);
    setSaving(true);

    try {
      const userId = await createProfile(displayName, avatarUrl, bio);
      setSuccess('Profile created! Now verify your wallet to link it.');
      await refreshProfile();
      setIsEditing(false);
    } catch (err) {
      setError('Failed to create profile: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateProfile = async () => {
    if (!profile?.userId) return;

    if (!displayName.trim()) {
      setError('Display name is required');
      return;
    }

    setError(null);
    setSaving(true);

    try {
      const { error: updateError } = await supabase
        .from('user_profile')
        .update({
          display_name: displayName,
          avatar_url: avatarUrl,
          bio: bio,
          updated_at: new Date().toISOString()
        })
        .eq('id', profile.userId);

      if (updateError) throw updateError;

      setSuccess('Profile updated successfully!');
      await refreshProfile();
      setIsEditing(false);
    } catch (err) {
      setError('Failed to update profile: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleVerified = async () => {
    setSuccess('Wallet verified successfully! 🎉');
    await refreshProfile();
    
    // Clear success message after 5 seconds
    setTimeout(() => setSuccess(null), 5000);
  };

  if (!walletAddress) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-gray-900 rounded-xl p-8 text-center">
          <h1 className="text-2xl font-bold mb-4">Profile Management</h1>
          <p className="text-gray-400 mb-6">
            Please connect your wallet to manage your profile
          </p>
        </div>
      </div>
    );
  }

  if (profileLoading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  const hasProfile = !!profile?.userId;
  const isVerified = profile?.verified;

  return (
    <div className="min-h-screen bg-black text-white p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Profile Management</h1>
          <p className="text-gray-400">
            Manage your profile and verify wallet ownership
          </p>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="mb-6 bg-green-900/50 border border-green-500 text-green-200 px-4 py-3 rounded-lg">
            {success}
          </div>
        )}
        {error && (
          <div className="mb-6 bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Profile Section */}
        <div className="bg-gray-900 rounded-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold">Profile Information</h2>
            {hasProfile && !isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors"
              >
                Edit Profile
              </button>
            )}
          </div>

          {!hasProfile && !isEditing ? (
            <div className="text-center py-8">
              <p className="text-gray-400 mb-4">You don't have a profile yet</p>
              <button
                onClick={() => setIsEditing(true)}
                className="px-6 py-3 rounded-lg bg-indigo-600 hover:bg-indigo-700 transition-colors font-medium"
              >
                Create Profile
              </button>
            </div>
          ) : isEditing ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Display Name *
                </label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Enter your display name"
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-indigo-500"
                  maxLength={50}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Avatar URL
                </label>
                <input
                  type="url"
                  value={avatarUrl}
                  onChange={(e) => setAvatarUrl(e.target.value)}
                  placeholder="https://example.com/avatar.jpg"
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Bio
                </label>
                <textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder="Tell us about yourself..."
                  rows={4}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-indigo-500 resize-none"
                  maxLength={500}
                />
                <div className="text-right text-sm text-gray-500 mt-1">
                  {bio.length}/500
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={hasProfile ? handleUpdateProfile : handleCreateProfile}
                  disabled={saving}
                  className="px-6 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  {saving ? 'Saving...' : hasProfile ? 'Update Profile' : 'Create Profile'}
                </button>
                {hasProfile && (
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      setDisplayName(profile.displayName || '');
                      setAvatarUrl(profile.avatarUrl || '');
                      setBio(profile.bio || '');
                      setError(null);
                    }}
                    className="px-6 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                {profile.avatarUrl && (
                  <img
                    src={profile.avatarUrl}
                    alt={profile.displayName}
                    className="w-20 h-20 rounded-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                )}
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-2xl font-bold">{profile.displayName}</h3>
                    {isVerified && <VerifiedBadge size="lg" verificationType={verificationType} />}
                  </div>
                  {profile.bio && (
                    <p className="text-gray-400 mt-2">{profile.bio}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Wallet Verification Section */}
        {hasProfile && (
          <div className="bg-gray-900 rounded-xl p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">Wallet Verification</h2>
            
            <div className="space-y-4">
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-400 mb-1">Current Wallet</p>
                    <p className="font-mono text-sm">{walletAddress}</p>
                    {isVerified && (
                      <div className="flex items-center gap-2 mt-2">
                        <VerifiedBadge size="sm" verificationType={verificationType} />
                        <span className="text-sm text-green-400">Verified</span>
                      </div>
                    )}
                  </div>
                  {!isVerified && (
                    <VerifyWalletButton
                      userId={profile.userId}
                      onVerified={handleVerified}
                    />
                  )}
                </div>
              </div>

              {!isVerified && (
                <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Why verify your wallet?</h4>
                  <ul className="text-sm text-gray-300 space-y-1 list-disc list-inside">
                    <li>Prove ownership of your wallet address</li>
                    <li>Get a verified badge on your profile</li>
                    <li>Link multiple wallets to one profile</li>
                    <li>Build trust with other users</li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Linked Wallets Section */}
        {hasProfile && (
          <div className="bg-gray-900 rounded-xl p-6">
            <LinkedWallets userId={profile.userId} isOwnProfile={true} />
          </div>
        )}
      </div>
    </div>
  );
}

